import React from "react";
import {
  IconClock,
  IconCpu,
  IconDatabase,
  IconDeviceDesktopAnalytics,
  IconGauge,
  IconServer2,
} from "@tabler/icons-react";
import type {
  BoardDocument,
  BoardVariantDocument,
  MediaAssetRecord,
  ModuleInstanceDocument,
  ModuleVersionRecord,
  SnapshotRecord,
  ThemeTokens,
} from "../../contracts/src/index";

export interface RenderContext {
  now: Date;
  theme: ThemeTokens;
  modules: ModuleVersionRecord[];
  snapshots: SnapshotRecord[];
  assets: MediaAssetRecord[];
  assetContent?: Record<string, string>;
  fixtureState?: string;
  previewStatus?: "live" | "fixture" | "stale" | "error";
}

export interface ResolvedModuleData {
  [key: string]: unknown;
}

export interface BoardViewProps {
  board: BoardDocument;
  variant: BoardVariantDocument;
  context: RenderContext;
  interactive?: boolean;
  selectedIds?: string[];
  onSelect?: (id: string, event: React.MouseEvent) => void;
  instanceClassName?: string;
  assetBaseUrl?: string;
}

export const runtimeCss = `
.sc-board, .sc-board * { box-sizing: border-box; }
.sc-board { position: relative; overflow: hidden; background: var(--sc-bg); color: var(--sc-fg); font-family: var(--sc-font), sans-serif; user-select: none; text-rendering: geometricPrecision; }
.sc-board--frame::before { content: ""; position: absolute; inset: var(--sc-frame-inset); border: 2px solid var(--sc-fg); border-radius: var(--sc-frame-radius); pointer-events: none; z-index: 9999; }
.sc-instance { position: absolute; min-width: 1px; min-height: 1px; overflow: hidden; }
.sc-instance[data-hidden="true"] { display: none; }
.sc-clock { width: 100%; height: 100%; display: flex; flex-direction: column; justify-content: flex-start; }
.sc-clock__time { font-family: var(--sc-numeric), var(--sc-font), sans-serif; font-size: 136px; font-weight: 400; line-height: .9; letter-spacing: -.075em; white-space: nowrap; }
.sc-clock__rule { width: 100%; height: 1px; background: var(--sc-divider); margin-top: 13px; }
.sc-clock__date { margin-top: 13px; font-size: 25px; line-height: 1.1; white-space: nowrap; }
.sc-weather-art { width: 100%; height: 100%; object-fit: contain; display: block; }
.sc-weather-summary { width: 100%; height: 100%; overflow: hidden; }
.sc-weather-summary__location { font-size: 25px; line-height: 1.2; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sc-weather-summary__temperature { margin-top: 11px; font-family: var(--sc-numeric), var(--sc-font), sans-serif; font-size: 61px; line-height: .84; letter-spacing: -.06em; white-space: nowrap; }
.sc-weather-summary__unit { font-size: 24px; line-height: 1; vertical-align: top; letter-spacing: 0; margin-left: 4px; }
.sc-weather-summary__condition { margin-top: 15px; font-size: 25px; line-height: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sc-weather-details { width: 100%; height: 100%; border-left: 1px solid var(--sc-divider); display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); grid-template-rows: repeat(2, minmax(0, 1fr)); }
.sc-weather-detail { min-width: 0; padding: 4px 8px 3px 15px; overflow: hidden; }
.sc-weather-detail:nth-child(2), .sc-weather-detail:nth-child(4) { border-left: 1px dotted var(--sc-muted); }
.sc-weather-detail:nth-child(-n + 2) { border-bottom: 1px dotted var(--sc-muted); }
.sc-weather-detail__label { font-size: 16px; line-height: 1.1; white-space: nowrap; }
.sc-weather-detail__value { margin-top: 7px; font-size: 20px; line-height: 1; white-space: nowrap; }
.sc-weather-detail__unit { font-size: 10px; }
.sc-server { width: 100%; height: 100%; padding: 0 0 0 0; overflow: hidden; }
.sc-server__head { height: 105px; display: flex; align-items: center; gap: 28px; border-bottom: 1px solid var(--sc-divider); padding: 0 0 0 0; }
.sc-server__head-icon { width: 64px; height: 72px; flex: 0 0 auto; }
.sc-server__title { font-size: 32px; line-height: 1; font-weight: 600; white-space: nowrap; }
.sc-server__dot { width: 25px; height: 25px; border-radius: 50%; margin-left: auto; margin-right: 1px; background: var(--sc-fg); }
.sc-server__rows { width: 100%; }
.sc-server__row { height: 66px; display: grid; grid-template-columns: 42px minmax(70px,1fr) 58px 140px; column-gap: 16px; align-items: center; border-bottom: 1px dotted var(--sc-muted); font-size: 24px; }
.sc-server__row:last-child { grid-template-columns: 42px minmax(70px,1fr) auto; border-bottom: 0; }
.sc-server__metric-icon { width: 42px; height: 42px; border: 2px solid var(--sc-fg); border-radius: 4px; display: grid; place-items: center; }
.sc-server__metric-icon--round { border-radius: 50%; }
.sc-server__metric-value { text-align: right; white-space: nowrap; font-size: 23px; }
.sc-meter { height: 16px; border: 1.5px solid var(--sc-fg); border-radius: 9px; overflow: hidden; background: var(--sc-bg); }
.sc-meter__fill { display: block; height: 100%; border-radius: 7px; background: var(--sc-fg); }
.sc-codex { width: 100%; height: 100%; overflow: hidden; padding: 8px 20px 0 20px; }
.sc-codex__heading { font-size: 27px; line-height: 1; font-weight: 600; border-bottom: 1px solid var(--sc-divider); padding-bottom: 5px; }
.sc-codex__bailian { position: relative; padding: 6px 0 5px; border-bottom: 1px solid var(--sc-divider); }
.sc-codex__bailian-name { font-size: 25px; line-height: 1; font-weight: 600; }
.sc-codex__bailian-amount { margin-top: 3px; font-size: 17px; line-height: 1; white-space: nowrap; }
.sc-codex__bailian-percent { position: absolute; top: 5px; right: 0; font-family: var(--sc-numeric), var(--sc-font), sans-serif; font-size: 32px; line-height: .9; letter-spacing: -.05em; white-space: nowrap; }
.sc-codex__bailian-bar { height: 14px; margin-top: 5px; border: 1.5px solid var(--sc-fg); border-radius: 5px; overflow: hidden; background: var(--sc-bg); }
.sc-codex__row { padding-top: 5px; }
.sc-codex__title-line { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.sc-codex__title { font-size: 25px; line-height: 1; font-weight: 600; letter-spacing: -.04em; white-space: nowrap; }
.sc-codex__codex-percent { flex: 0 0 auto; margin-top: -1px; font-family: var(--sc-numeric), var(--sc-font), sans-serif; font-size: 32px; line-height: .9; letter-spacing: -.05em; white-space: nowrap; }
.sc-codex__meta { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; margin-top: 4px; font-size: 16px; line-height: 1; white-space: nowrap; }
.sc-codex__quota { min-width: 0; overflow: hidden; text-overflow: ellipsis; }
.sc-codex__reset { flex: 0 0 auto; }
.sc-codex__bar { height: 16px; margin-top: 5px; border: 1.5px solid var(--sc-fg); border-radius: 5px; overflow: hidden; background: var(--sc-bg); }
.sc-codex__fill { height: 100%; background: repeating-linear-gradient(130deg, var(--sc-fg) 0 12px, var(--sc-bg) 12px 24px); }
.sc-codex__fill--solid { background: var(--sc-fg); }
.sc-divider { width: 100%; height: 100%; border-color: var(--sc-divider); }
.sc-text { width: 100%; height: 100%; overflow: hidden; white-space: pre-wrap; }
.sc-generic-image { width: 100%; height: 100%; display: block; }
.sc-module-error { width: 100%; height: 100%; display: grid; place-items: center; border: 1px dashed var(--sc-muted); color: var(--sc-muted); font-size: 14px; }
`;

function numberValue(value: unknown, fallback = 0): number {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function textValue(value: unknown, fallback = "--"): string {
  return value === null || value === undefined || value === "" ? fallback : String(value);
}

function booleanValue(value: unknown, fallback = true): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function clamp(value: number, minimum = 0, maximum = 100): number {
  return Math.max(minimum, Math.min(maximum, value));
}

function percentLabel(value: unknown): string {
  const parsed = numberValue(value, Number.NaN);
  if (!Number.isFinite(parsed)) return "--";
  return `${parsed.toFixed(parsed % 1 === 0 ? 0 : 1)}%`;
}

function amountLabel(value: unknown, currency: unknown): string {
  const parsed = numberValue(value, Number.NaN);
  if (!Number.isFinite(parsed)) return "剩余 --";
  const symbol = textValue(currency, "CNY") === "CNY" ? "¥" : `${textValue(currency, "CNY")} `;
  return `剩余 ${symbol}${parsed.toFixed(2)}`;
}

function readPath(input: Record<string, unknown>, path: string): unknown {
  return path.split(".").reduce<unknown>((current, part) => {
    if (!current || typeof current !== "object") return undefined;
    return (current as Record<string, unknown>)[part];
  }, input);
}

function moduleManifest(instance: ModuleInstanceDocument, context: RenderContext) {
  return context.modules.find((item) => item.id === instance.moduleVersionId)?.manifest;
}

function snapshotData(sourceInstanceId: string, context: RenderContext): Record<string, unknown> {
  const candidates = context.snapshots
    .filter((item) => item.sourceInstanceId === sourceInstanceId)
    .sort((a, b) => b.observedAt.localeCompare(a.observedAt));
  return candidates[0]?.data ?? {};
}

function findAssetByKey(key: string, context: RenderContext): MediaAssetRecord | undefined {
  return context.assets.find((asset) => asset.slug === `bender-weather-${key}` || asset.slug === key || asset.id === key);
}

export function resolveModuleData(instance: ModuleInstanceDocument, context: RenderContext, assetBaseUrl = ""): ResolvedModuleData {
  const manifest = moduleManifest(instance, context);
  const fixture = manifest?.fixtures[context.fixtureState ?? "normal"] ?? manifest?.fixtures.normal ?? {};
  const output: ResolvedModuleData = { ...fixture };
  for (const binding of instance.bindings) {
    const value = readPath(snapshotData(binding.sourceInstanceId, context), binding.path);
    if (value !== undefined) output[binding.input] = value;
  }
  if (manifest?.entry === "weather-illustration") {
    const key = textValue(output.assetKey, textValue(instance.props.fallbackAssetKey, "partly-cloudy"));
    const asset = findAssetByKey(key, context) ?? findAssetByKey(textValue(instance.props.fallbackAssetKey, "partly-cloudy"), context);
    if (asset) {
      output.assetKey = key;
      output.assetUrl = context.assetContent?.[asset.id] ?? `${assetBaseUrl}${asset.contentUrl}`;
    }
  }
  if (manifest?.entry === "image") {
    const id = textValue(instance.props.assetId, "");
    const asset = context.assets.find((candidate) => candidate.id === id);
    if (asset) output.assetUrl = context.assetContent?.[asset.id] ?? `${assetBaseUrl}${asset.contentUrl}`;
  }
  return output;
}

function localParts(date: Date, timezone: string): Record<string, string> {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date).reduce<Record<string, string>>((result, part) => {
    result[part.type] = part.value;
    return result;
  }, {});
}

function ClockModule({ instance, context }: ModuleProps) {
  const timezone = textValue(instance.props.timezone, "Asia/Shanghai");
  const parts = localParts(context.now, timezone);
  const weekday = new Intl.DateTimeFormat("zh-CN", { timeZone: timezone, weekday: "long" }).format(context.now);
  const showDate = booleanValue(instance.props.showDate, true);
  const showWeekday = booleanValue(instance.props.showWeekday, true);
  return <div className="sc-clock" style={{ textAlign: textValue(instance.props.align, "left") as React.CSSProperties["textAlign"] }}>
    <div className="sc-clock__time">{parts.hour}:{parts.minute}</div>
    <div className="sc-clock__rule" />
    {showDate && <div className="sc-clock__date">{parts.year}-{parts.month}-{parts.day}{showWeekday ? ` ${weekday}` : ""}</div>}
  </div>;
}

interface ModuleProps {
  instance: ModuleInstanceDocument;
  data: ResolvedModuleData;
  context: RenderContext;
}

function WeatherIllustrationModule({ instance, data }: ModuleProps) {
  const src = textValue(data.assetUrl, "");
  if (!src) return <div className="sc-module-error">天气插画不可用</div>;
  return <img className="sc-weather-art" src={src} alt={textValue(data.condition, "天气插画")} style={{ objectFit: textValue(instance.props.objectFit, "contain") as React.CSSProperties["objectFit"] }} />;
}

function WeatherSummaryModule({ instance, data }: ModuleProps) {
  const decimals = clamp(numberValue(instance.props.temperatureDecimals, 1), 0, 1);
  const temperature = numberValue(data.temperature).toFixed(decimals);
  return <div className="sc-weather-summary">
    <div className="sc-weather-summary__location">{textValue(data.location)}</div>
    <div className="sc-weather-summary__temperature">{temperature}{booleanValue(instance.props.showUnit, true) && <span className="sc-weather-summary__unit">°C</span>}</div>
    <div className="sc-weather-summary__condition">{textValue(data.condition)}</div>
  </div>;
}

function WeatherDetailsModule({ instance, data }: ModuleProps) {
  const items = [
    ["showHumidity", "湿度", `${numberValue(data.humidity)}%`, ""],
    ["showWind", "风速", numberValue(data.windSpeed).toFixed(1), " km/h"],
    ["showLow", "今日最低", `${numberValue(data.low).toFixed(1)}°`, ""],
    ["showHigh", "今日最高", `${numberValue(data.high).toFixed(1).replace(/\.0$/, "")}°`, ""],
  ].filter(([key]) => booleanValue(instance.props[String(key)], true));
  return <div className="sc-weather-details">
    {items.map(([key, label, value, unit]) => <div className="sc-weather-detail" key={String(key)}>
      <div className="sc-weather-detail__label">{label}</div>
      <div className="sc-weather-detail__value">{value}<span className="sc-weather-detail__unit">{unit}</span></div>
    </div>)}
  </div>;
}

const metricIcons = {
  cpu: IconCpu,
  memory: IconDeviceDesktopAnalytics,
  disk: IconDatabase,
  load: IconGauge,
  uptime: IconClock,
};

function MetricIcon({ kind, round = false }: { kind: keyof typeof metricIcons; round?: boolean }) {
  const Icon = metricIcons[kind];
  return <span className={`sc-server__metric-icon${round ? " sc-server__metric-icon--round" : ""}`}><Icon size={25} stroke={2} /></span>;
}

function ServerStatusModule({ instance, data }: ModuleProps) {
  const metrics = [
    { prop: "showCpu", kind: "cpu" as const, label: "CPU", value: `${numberValue(data.cpu)}%`, percent: numberValue(data.cpu) },
    { prop: "showMemory", kind: "memory" as const, label: "内存", value: `${numberValue(data.memory)}%`, percent: numberValue(data.memory) },
    { prop: "showDisk", kind: "disk" as const, label: "磁盘", value: `${numberValue(data.disk)}%`, percent: numberValue(data.disk) },
    { prop: "showLoad", kind: "load" as const, label: "负载", value: textValue(data.load, "0"), percent: numberValue(data.load) * 100 },
  ].filter((metric) => booleanValue(instance.props[metric.prop], true));
  const healthy = booleanValue(data.healthy, true);
  return <div className="sc-server">
    <div className="sc-server__head">
      <IconServer2 className="sc-server__head-icon" stroke={1.7} />
      <div className="sc-server__title">{textValue(instance.props.title, healthy ? "服务器正常" : "服务器异常")}</div>
      {booleanValue(instance.props.showStatusDot, true) && <span className="sc-server__dot" />}
    </div>
    <div className="sc-server__rows">
      {metrics.map((metric) => <div className="sc-server__row" key={metric.kind}>
        <MetricIcon kind={metric.kind} />
        <span>{metric.label}</span>
        <span className="sc-server__metric-value">{metric.value}</span>
        <span className="sc-meter"><span className="sc-meter__fill" style={{ width: `${clamp(metric.percent)}%` }} /></span>
      </div>)}
      {booleanValue(instance.props.showUptime, true) && <div className="sc-server__row">
        <MetricIcon kind="uptime" round />
        <span>运行</span>
        <span className="sc-server__metric-value">{textValue(data.uptime)}</span>
      </div>}
    </div>
  </div>;
}

function CodexQuotaModule({ instance, data }: ModuleProps) {
  const remaining = data.remainingPercent === null || data.remainingPercent === undefined ? null : clamp(numberValue(data.remainingPercent));
  const bailianRemaining = data.bailianRemainingPercent === null || data.bailianRemainingPercent === undefined
    ? null
    : clamp(numberValue(data.bailianRemainingPercent));
  const solid = textValue(instance.props.barStyle, "zebra") === "solid";
  return <div className="sc-codex">
    <div className="sc-codex__heading">额度</div>
    <div className="sc-codex__bailian">
      <div className="sc-codex__bailian-name">百炼</div>
      <div className="sc-codex__bailian-amount">{amountLabel(data.bailianRemainingAmount, data.bailianCurrency)}</div>
      <div className="sc-codex__bailian-percent">{percentLabel(bailianRemaining)}</div>
      <div className="sc-codex__bailian-bar"><div className={`sc-codex__fill${solid ? " sc-codex__fill--solid" : ""}`} style={{ width: `${bailianRemaining ?? 0}%` }} /></div>
    </div>
    <div className="sc-codex__row">
      <div className="sc-codex__title-line">
        <div className="sc-codex__title">{textValue(instance.props.titlePrefix, "Codex /")} {textValue(data.plan, "Plus")}</div>
        <div className="sc-codex__codex-percent">{percentLabel(remaining)}</div>
      </div>
      <div className="sc-codex__meta">
        <div className="sc-codex__quota">周额度剩余 {remaining === null ? "--" : percentLabel(remaining)}</div>
        {booleanValue(instance.props.showReset, true) && <div className="sc-codex__reset">重置&nbsp;&nbsp;{textValue(data.resetLabel)}</div>}
      </div>
      <div className="sc-codex__bar"><div className={`sc-codex__fill${solid ? " sc-codex__fill--solid" : ""}`} style={{ width: `${remaining ?? 0}%` }} /></div>
    </div>
  </div>;
}

function DividerModule({ instance }: ModuleProps) {
  const vertical = textValue(instance.props.orientation, "horizontal") === "vertical";
  const style = textValue(instance.props.style, "solid") as "solid" | "dashed" | "dotted";
  const weight = numberValue(instance.props.weight, 1);
  return <div className="sc-divider" style={vertical ? { borderLeftWidth: weight, borderLeftStyle: style } : { borderTopWidth: weight, borderTopStyle: style }} />;
}

function TextModule({ instance, data }: ModuleProps) {
  return <div className="sc-text" style={{ fontSize: numberValue(instance.props.fontSize, 24), fontWeight: numberValue(instance.props.fontWeight, 500), textAlign: textValue(instance.props.align, "left") as React.CSSProperties["textAlign"] }}>
    {textValue(data.value, textValue(instance.props.text, "文本"))}
  </div>;
}

function ImageModule({ instance, data }: ModuleProps) {
  const src = textValue(data.assetUrl, "");
  if (!src) return <div className="sc-module-error">请选择图片资产</div>;
  return <img className="sc-generic-image" src={src} alt="" style={{ objectFit: textValue(instance.props.objectFit, "contain") as React.CSSProperties["objectFit"] }} />;
}

function ModuleContent(props: ModuleProps) {
  const entry = moduleManifest(props.instance, props.context)?.entry;
  if (entry === "clock") return <ClockModule {...props} />;
  if (entry === "weather-illustration") return <WeatherIllustrationModule {...props} />;
  if (entry === "weather-summary") return <WeatherSummaryModule {...props} />;
  if (entry === "weather-details") return <WeatherDetailsModule {...props} />;
  if (entry === "server-status") return <ServerStatusModule {...props} />;
  if (entry === "codex-quota") return <CodexQuotaModule {...props} />;
  if (entry === "divider") return <DividerModule {...props} />;
  if (entry === "text") return <TextModule {...props} />;
  if (entry === "image") return <ImageModule {...props} />;
  return <div className="sc-module-error">未知模块：{entry ?? props.instance.moduleVersionId}</div>;
}

export function BoardView({ board, variant, context, interactive = false, selectedIds = [], onSelect, instanceClassName = "", assetBaseUrl = "" }: BoardViewProps) {
  const style = {
    width: variant.canvas.width,
    height: variant.canvas.height,
    "--sc-bg": context.theme.colors.background,
    "--sc-fg": context.theme.colors.foreground,
    "--sc-muted": context.theme.colors.muted,
    "--sc-divider": context.theme.colors.divider,
    "--sc-font": context.theme.typography.sans,
    "--sc-numeric": context.theme.typography.numeric,
    "--sc-frame-inset": `${variant.canvas.frameInset}px`,
    "--sc-frame-radius": `${variant.canvas.frameRadius}px`,
  } as React.CSSProperties;
  return <div
    className={`sc-board${variant.canvas.showFrame ? " sc-board--frame" : ""}`}
    style={style}
    data-board-id={board.id}
    data-render-ready="true"
  >
    <style>{runtimeCss}</style>
    {[...variant.instances].sort((a, b) => a.frame.z - b.frame.z).map((item) => {
      const data = resolveModuleData(item, context, assetBaseUrl);
      const selected = selectedIds.includes(item.id);
      return <div
        key={item.id}
        className={`sc-instance ${instanceClassName}${selected ? " is-selected" : ""}`.trim()}
        data-instance-id={item.id}
        data-hidden={item.frame.visible === false ? "true" : "false"}
        data-locked={item.frame.locked ? "true" : "false"}
        onMouseDown={interactive ? (event) => onSelect?.(item.id, event) : undefined}
        style={{
          left: item.frame.x,
          top: item.frame.y,
          width: item.frame.width,
          height: item.frame.height,
          zIndex: item.frame.z,
          transform: item.frame.rotation ? `rotate(${item.frame.rotation}deg)` : undefined,
          pointerEvents: interactive ? "auto" : "none",
        }}
      >
        <ModuleContent instance={item} data={data} context={context} />
      </div>;
    })}
  </div>;
}
